/**
 * AppShell — StreamPersona root layout.
 *
 * Structure:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  Top Tab Bar (Face Swap / Voice / Call / Settings)  │
 *   ├──────────────┬──────────────────────┬───────────────┤
 *   │  Left Panel  │    Center Content    │  Right Panel  │
 *   │  (240px)     │                      │  (280px)      │
 *   ├──────────────┴──────────────────────┴───────────────┤
 *   │  Bottom Status Bar                                  │
 *   └─────────────────────────────────────────────────────┘
 */

import { useState, useCallback, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { TabBar, type AppTab } from "./TabBar";
import { StatusBar } from "./StatusBar";
import { FaceSwapTab } from "../tabs/FaceSwapTab";
import { VoiceTab } from "../tabs/VoiceTab";
import { SettingsTab } from "../tabs/SettingsTab";
import { BackendErrorScreen } from "./BackendErrorScreen";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import { useBackendHealth } from "@/hooks/useBackendHealth";
import { useSessionDiscovery } from "@/hooks/useSessionDiscovery";
import { useProjector } from "@/hooks/useProjector";
import { getToken } from "@/lib/tokenStore";
import { API_BASE, auditLog } from "@/lib/api";
import { getTransport } from "@/lib/transport";
import { isLiteEdition } from "@/lib/config";

import { TopUpCreditsModal } from "../checkout/TopUpCreditsModal";
import { clearFaceVault } from "@/lib/faceVault";
import { LiveKitSession } from "@/hooks/useSessionDiscovery";

function AppShellContent({ livekitSession }: { livekitSession?: LiveKitSession | null }) {
  const [activeTab, setActiveTab] = useState<AppTab>("faceswap");
  const [leftCollapsed,  setLeftCollapsed]  = useState(false);
  const [rightCollapsed, setRightCollapsed] = useState(false);
  const [isTopUpOpen,    setIsTopUpOpen]    = useState(false);

  // Shared state passed down to tabs
  const [processedStream, setProcessedStream] = useState<MediaStream | null>(null);
  const { openProjector, isProjectorOpen, updateProjectorStream } = useProjector();

  useEffect(() => {
    updateProjectorStream(processedStream);
  }, [processedStream, updateProjectorStream]);
  const [swapping,  setSwapping]  = useState(false);
  const [micActive, setMicActive] = useState(false);
  const [activeProfileName, setActiveProfileName] = useState<string | null>(null);
  const [activeVoiceName,   setActiveVoiceName]   = useState<string | null>(null);
  const [fps, setFps] = useState(0);
  const { isMobile } = useBreakpoint();


  const handleClearSession = useCallback(async () => {
    if (!window.confirm("Are you sure you want to wipe all uploaded faces, voice clips, and end any active calls? This resets the session.")) {
      auditLog("FRONTEND_CLEAR_SESSION_CANCELLED");
      return;
    }
    try {
      await auditLog("FRONTEND_CLEAR_SESSION_CONFIRMED");
      // 1. Stop any active local webcam tracks to release camera hardware
      try {
        const videos = document.querySelectorAll("video");
        videos.forEach(v => {
          const stream = v.srcObject as MediaStream | null;
          stream?.getTracks().forEach(t => t.stop());
          v.srcObject = null;
        });
      } catch (e) {
        console.error("Failed to release camera hardware tracks:", e);
      }

      // 2. Wipe client-side IndexedDB face vault and localStorage
      try {
        await clearFaceVault();
      } catch (vaultErr) {
        console.warn("Failed to clear face vault:", vaultErr);
      }
      try {
        localStorage.removeItem("streampersona_active_profile");
        localStorage.removeItem("streampersona_profiles");
      } catch {}

      // 3. Clear session via Unified Transport or Local Backend
      if (isLiteEdition()) {
        const transport = getTransport();
        await transport.clearSession().catch(() => {});
        alert("Session cleared successfully! Wiping app memory...");
        window.location.reload();
      } else {
        const res = await fetch(`${API_BASE()}/api/system/clear-session`, { method: "POST" });
        if (res.ok) {
          alert("Session cleared successfully! Wiping app memory...");
          window.location.reload();
        } else {
          alert("Wipe request failed: " + res.statusText);
        }
      }
    } catch (err) {
      alert("Error resetting session: " + err);
    }
  }, []);

  return (
    <div style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      width: "100vw",
      background: "var(--bg-base)",
      overflow: "hidden",
    }}>
      {/* Top Tab Bar */}
      <TabBar
        activeTab={activeTab}
        onTabChange={setActiveTab}
        leftCollapsed={leftCollapsed}
        rightCollapsed={rightCollapsed}
        onToggleLeft={() => setLeftCollapsed(v => !v)}
        onToggleRight={() => setRightCollapsed(v => !v)}
        isMobile={isMobile}
        onClearSession={handleClearSession}
        onOpenTopUp={() => setIsTopUpOpen(true)}
        onOpenProjector={() => openProjector(processedStream)}
        isProjectorOpen={isProjectorOpen}
      />

      {/* Main content area */}
      <div style={{ flex: 1, overflow: isMobile ? "auto" : "hidden", display: "flex", flexDirection: "column" }}>
        {activeTab === "faceswap" && (
          <FaceSwapTab
            leftCollapsed={leftCollapsed}
            rightCollapsed={rightCollapsed}
            isMobile={isMobile}
            onSwappingChange={setSwapping}
            onProcessedStream={setProcessedStream}
            onActiveProfileChange={setActiveProfileName}
            onFpsChange={setFps}
            livekitSession={livekitSession}
            onOpenProjector={() => openProjector(processedStream)}
            isProjectorOpen={isProjectorOpen}
          />
        )}
        {activeTab === "settings" && (
          <SettingsTab />
        )}
      </div>

      {/* Bottom Status Bar */}
      <StatusBar
        micActive={micActive}
        swapping={swapping}
        activeProfileName={activeProfileName}
        activeVoiceName={activeVoiceName}
        fps={fps}
        onOpenTopUp={() => setIsTopUpOpen(true)}
        isMobile={isMobile}
      />

      {/* Top Up Credits Modal */}
      <TopUpCreditsModal
        isOpen={isTopUpOpen}
        onClose={() => setIsTopUpOpen(false)}
        transport={getTransport()}
      />
    </div>
  );

}

function AppShellWithHealth({ onRetryDiscovery }: { onRetryDiscovery: () => void }) {
  const health = useBackendHealth();

  if (health.status === "unreachable") {
    return (
      <BackendErrorScreen
        title="GPU Node Connecting..."
        message="The cloud GPU streaming node is initializing its secure tunnel. Please wait a moment."
        icon={
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto" }}>
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        }
        onRetry={() => {
          onRetryDiscovery();
          window.location.reload();
        }}
      />
    );
  }

  if (health.status === "checking") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--bg-base)",
          color: "var(--text-secondary)",
          fontSize: "var(--text-sm)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-3)" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
          </div>
          <div>Connecting to cloud GPU node...</div>
        </div>
      </div>
    );
  }

  return <AppShellContent />;
}

/** AppShell with license-first gate, dynamic session discovery, and backend health check */
export function AppShell() {
  const token = getToken();

  // 1. License-First Gate: if unlicensed, go straight to activation page
  if (!token || !token.licenseKey) {
    return <Navigate to="/activate" replace />;
  }

  // 2. Dynamic Discovery Gate: find active GPU node session from platform-core
  const discovery = useSessionDiscovery();

  if (discovery.status === "idle" || discovery.status === "discovering") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--bg-base)",
          color: "var(--text-secondary)",
          fontSize: "var(--text-sm)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "var(--space-3)" }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
          </div>
          <div>{discovery.message || "Connecting to StreamPersona platform..."}</div>
        </div>
      </div>
    );
  }

  if (discovery.status === "provisioning") {
    return (
      <BackendErrorScreen
        title="Starting Cloud GPU Node"
        message={discovery.message || "Starting your dedicated cloud GPU node..."}
        icon={
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto" }}>
            <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
          </svg>
        }
        isProvisioning={true}
        bootStage={discovery.bootStage}
        bootProgress={discovery.bootProgress}
        eta={discovery.eta}
        onRetry={discovery.retry}
      />
    );
  }

  if (discovery.status === "collision") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          background: "var(--bg-base)",
          padding: "var(--space-4)",
        }}
      >
        <div
          style={{
            maxWidth: 480,
            width: "100%",
            background: "var(--bg-surface)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-lg)",
            padding: "var(--space-6)",
            textAlign: "center",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 8px 10px -6px rgba(0, 0, 0, 0.5)",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: "rgba(245, 158, 11, 0.1)",
              color: "#f59e0b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto var(--space-4)",
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
              <line x1="8" y1="21" x2="16" y2="21" />
              <line x1="12" y1="17" x2="12" y2="21" />
            </svg>
          </div>
          <h2 style={{ fontSize: "var(--text-lg)", fontWeight: 600, color: "var(--text-primary)", marginBottom: "var(--space-2)" }}>
            Stream Active on Another Device
          </h2>
          <p style={{ fontSize: "var(--text-sm)", color: "var(--text-secondary)", lineHeight: 1.5, marginBottom: "var(--space-6)" }}>
            StreamPersona is currently streaming on your other authorized device. Your single-seat license allows up to three personal devices with one active live stream at a time.
          </p>
          <div style={{ display: "flex", gap: "var(--space-3)", justifyContent: "center" }}>
            <button
              onClick={() => discovery.cancelCollision?.()}
              style={{
                padding: "8px 16px",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-subtle)",
                background: "transparent",
                color: "var(--text-secondary)",
                fontSize: "var(--text-sm)",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => discovery.requestTakeover?.()}
              style={{
                padding: "8px 16px",
                borderRadius: "var(--radius-md)",
                border: "none",
                background: "var(--accent)",
                color: "#ffffff",
                fontSize: "var(--text-sm)",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              End Other Stream &amp; Switch Here
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (discovery.status === "error") {
    return (
      <BackendErrorScreen
        title="Platform Connection"
        message={discovery.message || "Failed to locate active GPU streaming node."}
        icon={
          <svg width="42" height="42" viewBox="0 0 24 24" fill="none" stroke="var(--status-error)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto" }}>
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        }
        isProvisioning={false}
        onRetry={discovery.retry}
      />
    );
  }

  // Lite mode logic (when session is ready)
  if (discovery.status === "ready" && discovery.session) {
    return <AppShellContent livekitSession={discovery.session} />;
  }

  // 3. Health Gate: if Studio mode, check backend health on the active tunnel
  // For Lite mode, we skip useBackendHealth
  return <AppShellContent />;
}
